import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { getWeekDayLabel } from '../../../../../utils/date';
import { showChat } from '../../../../../utils/liveChat';
import { isFreePlan } from '../../../../../utils/subscription';
import Button from '../../../../common/base/Button';
import { StyledCard } from '../../../../common/base/Card';
import Container from '../../../../common/base/Container';
import Divider from '../../../../common/base/Divider';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import { useDialog } from '../../../../common/Dialog';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import SwitchInput from '../../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import TimezoneSelectInput, { BROWSER_TIMEZONE_OPTION } from '../../../../common/Form/Fields/TimezoneSelectInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { UserContext } from '../../../../contexts/UserContext';
import { useDatePreferences, useUpdateFirstDayOfWeekPreference, useUpdateTimezonePreference } from '../../../../hooks/useDatePreferences';
import { DashboardContext } from '../../DashboardContext';
import AccountAuthentication from './AccountAuthentication';
import { AccountMultiFactorAuthentication } from './AccountMultiFactorAuthentication';
const AUTOMATIC_DATE_PREFERENCE = BROWSER_TIMEZONE_OPTION;
const MANUAL_FIRST_DAY_OF_WEEK_OPTIONS = [{
  value: '0',
  label: 'Sunday'
}, {
  value: '1',
  label: 'Monday'
}];
const AccountProfile: React.FC = () => {
  const $ = _c(71);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    user,
    mutateUser
  } = useContext(UserContext);
  const {
    addToast
  } = useToasts();
  const {
    subscription,
    organizations,
    logout
  } = useContext(DashboardContext);
  const showDialog = useDialog();
  const {
    first_day_of_week,
    locale_first_day_of_week,
    timezone,
    uses_browser_timezone,
    uses_locale_first_day_of_week
  } = useDatePreferences();
  const updateTimezonePreference = useUpdateTimezonePreference();
  const updateFirstDayOfWeekPreference = useUpdateFirstDayOfWeekPreference();
  let t0;
  if ($[0] !== locale_first_day_of_week) {
    t0 = getWeekDayLabel(locale_first_day_of_week);
    $[0] = locale_first_day_of_week;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = `Browser default (${t0})`;
  let t2;
  if ($[2] !== t1) {
    t2 = [{
      value: AUTOMATIC_DATE_PREFERENCE,
      label: t1
    }, ...MANUAL_FIRST_DAY_OF_WEEK_OPTIONS];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const first_day_of_week_options = t2;
  let t3;
  if ($[4] !== organizations || $[5] !== subscription) {
    t3 = organizations?.some(org => org?.plan && !isFreePlan(org?.plan) && org?.role === "owner" && (!subscription?.cancel_at || new Date(subscription.cancel_at).getTime() > Date.now()));
    $[4] = organizations;
    $[5] = subscription;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const user_owns_paid_orgs = !!t3;
  const user_is_deletable = !user?.workos_profile_id && !user_owns_paid_orgs;
  let t4;
  if ($[7] !== HookdeckAPI.users || $[8] !== addToast || $[9] !== logout || $[10] !== showDialog || $[11] !== user.email) {
    t4 = () => showDialog(() => HookdeckAPI.users.delete().then(() => {
      logout();
    }).catch(e => {
      const error_message = e?.response?.message || "The account could not be deleted, please contact us.";
      addToast("error", error_message);
    }), undefined, {
      title: "Delete account",
      submit_label: "Delete account",
      cancel_label: "Cancel",
      submit_icon: "delete",
      danger: true,
      form_props: {
        initial_values: {
          name: ""
        },
        validate: t5 => {
          const {
            name
          } = t5;
          if (!name || name.length < 0) {
            return {
              name: "Required"
            };
          }
          if (name !== user?.email) {
            return {
              name: "Account email does not match."
            };
          }
          return {};
        },
        Fields: () => <><Text m={{
            b: 4
          }}>This action can't be undone. If you delete your account, any organizations that you are the only member of will also be deleted. You'll lose your entire billing history, as well as the projects within those organizations and all of their associated data. Are you sure?<br /><br />If you’d like to delete this account, type <strong>{user?.email}</strong> below to confirm.</Text><TextInput m={0} name="name" placeholder={user?.email} required={true} /></>
      }
    });
    $[7] = HookdeckAPI.users;
    $[8] = addToast;
    $[9] = logout;
    $[10] = showDialog;
    $[11] = user.email;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const onDelete = t4;
  let t5;
  if ($[13] !== addToast || $[14] !== mutateUser) {
    t5 = promise => promise.then(user_0 => {
      mutateUser && mutateUser(user_0);
      addToast("success", "Profile updated");
    }).catch(() => {
      addToast("error", "Failed to update profile");
    });
    $[13] = addToast;
    $[14] = mutateUser;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleSubmit = t5;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      b: 10
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== user.email || $[18] !== user.name) {
    t7 = {
      name: user.name,
      email: user.email
    };
    $[17] = user.email;
    $[18] = user.name;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== HookdeckAPI.session || $[21] !== handleSubmit) {
    t8 = values_0 => handleSubmit(HookdeckAPI.session.updateMe({
      name: values_0.name
    }));
    $[20] = HookdeckAPI.session;
    $[21] = handleSubmit;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== user.name) {
    t9 = props => <Form><Div m={{
        b: 10
      }}><Text bold={true} size="l" as="h2" m={{
          t: 0,
          b: 4
        }}>Your name</Text><TextInput name="name" required={true} m={{
          b: 5
        }} /><Button disabled={!props.isValid || !props.touched || props.isSubmitting || user.name === props.values.name} primary={true} submit={true} icon={props.isSubmitting ? "loading" : "save"}>Save</Button></Div><Div m={{
        b: 10
      }}><Text bold={true} size="l" as="h2" m={{
          t: 0,
          b: 4
        }}>Your email</Text><TextInput disabled={true} name="email" type="email" help="Contact us to change your email" m={{
          b: 5
        }} /><Button outline={true} icon="chat" type="button" onClick={_temp2}>Contact us</Button></Div></Form>;
    $[23] = user.name;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = <Div m={t6}><Formik initialValues={t7} validate={_temp} onSubmit={t8}>{t9}</Formik></Div>;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <AccountAuthentication />;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t10) {
    t12 = <Container medium={true} left={true}>{t10}{t11}</Container>;
    $[30] = t10;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      b: 10
    };
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = uses_browser_timezone ? AUTOMATIC_DATE_PREFERENCE : timezone;
  const t15 = uses_locale_first_day_of_week ? AUTOMATIC_DATE_PREFERENCE : String(first_day_of_week);
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = {
      timezone: t14,
      first_day_of_week: t15
    };
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== addToast || $[37] !== updateFirstDayOfWeekPreference || $[38] !== updateTimezonePreference) {
    t17 = async values_1 => {
      const timezone_preference = values_1.timezone === AUTOMATIC_DATE_PREFERENCE ? null : values_1.timezone;
      const first_day_of_week_preference = values_1.first_day_of_week === AUTOMATIC_DATE_PREFERENCE ? null : Number(values_1.first_day_of_week) as 0 | 1;
      try {
        await Promise.all([updateTimezonePreference(timezone_preference), updateFirstDayOfWeekPreference(first_day_of_week_preference)]);
        addToast("success", "Profile updated");
      } catch {
        addToast("error", "Failed to update profile");
      }
    };
    $[36] = addToast;
    $[37] = updateFirstDayOfWeekPreference;
    $[38] = updateTimezonePreference;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== first_day_of_week_options) {
    t18 = props_0 => <Form><Text bold={true} size="l" as="h2" m={{
        t: 0,
        b: 1
      }}>Date and time</Text><Text muted={true} m={{
        t: 0,
        b: 4
      }}>Dates throughout the dashboard use these preferences. Browser defaults continue to follow your current browser settings.</Text><Div grid={{
        columns: "minmax(0, 2fr) minmax(0, 1fr)",
        gap: 4
      }} m={{
        b: 5
      }}><TimezoneSelectInput name="timezone" /><SelectInput block={true} name="first_day_of_week" label="First day of week" options={first_day_of_week_options} /></Div><Button disabled={!props_0.dirty || props_0.isSubmitting} primary={true} submit={true} icon={props_0.isSubmitting ? "loading" : "save"}>Save</Button></Form>;
    $[40] = first_day_of_week_options;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t16 || $[43] !== t17 || $[44] !== t18) {
    t19 = <Container medium={true} left={true}><Div m={t13}><Formik enableReinitialize={true} initialValues={t16} onSubmit={t17}>{t18}</Formik></Div></Container>;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      b: 10
    };
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const t21 = !user.disable_alerts;
  let t22;
  if ($[47] !== t21) {
    t22 = {
      enable_alerts: t21
    };
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== HookdeckAPI.session || $[50] !== handleSubmit) {
    t23 = values_2 => handleSubmit(HookdeckAPI.session.updateMe({
      disable_alerts: !values_2.enable_alerts
    }));
    $[49] = HookdeckAPI.session;
    $[50] = handleSubmit;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t22 || $[53] !== t23) {
    t24 = <Container medium={true} left={true}><Div m={t20}><Formik initialValues={t22} onSubmit={t23}>{_temp3}</Formik></Div></Container>;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Div m={{
      b: 8
    }} p={{
      x: 6
    }}><Divider /></Div>;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Container medium={true} left={true}><AccountMultiFactorAuthentication /></Container>;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Div m={{
      b: 8
    }} p={{
      x: 6
    }}><Divider /></Div>;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      b: 20
    };
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Div m={{
      b: 4
    }}><Text bold={true} size="l" as="h2" m={0}>Delete account</Text><Text muted={true}>Permanently delete your account and all associated account data.</Text></Div>;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  const t30 = !user_is_deletable;
  let t31;
  if ($[60] !== onDelete || $[61] !== t30) {
    t31 = <Button disabled={t30} outline={true} danger={true} icon="delete" onClick={onDelete}>Delete account</Button>;
    $[60] = onDelete;
    $[61] = t30;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== t31 || $[64] !== user_is_deletable) {
    t32 = <Container medium={true} left={true} m={t28}>{t29}<Tooltip disabled={user_is_deletable} tooltip="You're the owner of an organization with a paid plan. In order to delete your account, you'll first need to either downgrade the organization to a free plan, or transfer ownership of the organization to a member within it.">{t31}</Tooltip></Container>;
    $[63] = t31;
    $[64] = user_is_deletable;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== t12 || $[67] !== t19 || $[68] !== t24 || $[69] !== t32) {
    t33 = <>{t12}{t19}{t24}{t25}{t26}{t27}{t32}</>;
    $[66] = t12;
    $[67] = t19;
    $[68] = t24;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  return t33;
};
export default AccountProfile;
function _temp(values) {
  const errors = {};
  if (!values.name || values.name.length === 0) {
    errors.name = "Required";
  }
  return errors;
}
function _temp2() {
  return showChat();
}
function _temp3(props_1) {
  return <Form><Text bold={true} size="l" as="h2" m={{
      b: 4
    }}>Notifications</Text><StyledCard p={{
      x: 4,
      y: 3
    }} flex={{
      justify: "flex-start",
      align: "center",
      gap: 3
    }} m={{
      b: 4
    }}><SwitchInput name="enable_alerts" onChange={() => props_1.submitForm()} /><Div flex={{
        align: "center",
        gap: 1
      }}><Text>Email notifications</Text></Div></StyledCard></Form>;
}